import BarChartIcon from '@mui/icons-material/BarChart';
import DefaultIcon from '@mui/icons-material/ViewList';
import { Box, Divider } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  DashboardMenuItem,
  Link,
  Menu as RMenu,
  useResourceDefinitions,
  useSidebarState,
} from 'react-admin';
import { useLocation } from 'react-router-dom';
import logo from '../assets/img/artic-logo.svg';
import { useAppContext } from '../contexts/AppContext';

import { submenues, useDynamicResources } from '../resources/Resources';
import SubMenu from './SubMenu';

const useStyles = makeStyles({
  logo: {
    height: '40px',
    width: '145px',
    marginBottom: 10,
    marginTop: 5,
  },
  menuItem: {
    width: '100%',
  },
});

const RenderItem = ({ resource, show = false }) => {
  const classes = useStyles();
  if (!resource?.hasList && !show) {
    return null;
  }
  return (
    <RMenu.ResourceItem
      key={resource?.name}
      className={classes.menuItem}
      name={resource?.name}
      primaryText={
        (resource?.options && resource.options.label) || resource?.name
      }
      leftIcon={
        resource?.icon && typeof resource.icon === 'function' ? (
          <resource.icon />
        ) : (
          <DefaultIcon />
        )
      }
    />
  );
};

const RenderSubmenu = ({
  allResources,
  resources,
  name,
  label,
  icon,
  open,
  handleToggle,
  state,
}) => {
  const classes = useStyles();
  const list = allResources?.filter(i => resources?.includes(i?.name));
  const location = useLocation();
  const currentPath = location.pathname.split('/')[1];
  const isActive = list?.some(resource => resource.name === currentPath);

  return list?.length > 0 ? (
    <SubMenu
      child
      icon={icon ? icon : <DefaultIcon width="24px" height="24px" />}
      name={label}
      isOpen={state[name]}
      handleToggle={() => handleToggle(name)}
      active={isActive}
      sidebarIsOpen={open}
    >
      {list?.map((resource, key) => (
        <RMenu.Item
          key={key}
          className={classes.menuItem}
          to={`/${resource?.name}`}
          primaryText={
            (resource?.options && resource.options.label) || resource?.name
          }
          leftIcon={resource?.icon ? <resource.icon /> : <DefaultIcon />}
        />
      ))}
    </SubMenu>
  ) : null;
};

export const Menu = ({ onMenuClick, _dense = false }) => {
  const classes = useStyles();
  const location = useLocation();
  const pathname = location.pathname;
  const resourcesDefinitions = useResourceDefinitions();
  const { currentApp } = useAppContext();
  const {
    getDefaultResources: getDynamicDefaultResources,
    getSubMenuMetrics: getDynamicSubMenuMetrics,
  } = useDynamicResources();

  // Memoizar allResources para que no cambie en cada render
  const allResources = useMemo(
    () =>
      Object.keys(resourcesDefinitions).map(name => resourcesDefinitions[name]),
    [resourcesDefinitions]
  );

  const [_open] = useSidebarState();

  // Obtener recursos dinámicamente según la aplicación actual
  const dynamicDefaultResources = useMemo(
    () => getDynamicDefaultResources(),
    [currentApp, getDynamicDefaultResources] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const dynamicSubMenuMetrics = useMemo(
    () => getDynamicSubMenuMetrics(),
    [currentApp, getDynamicSubMenuMetrics] //eslint-disable-line react-hooks/exhaustive-deps
  );

  // Función memoizada para obtener el estado activo
  const getActiveMenuState = useCallback(() => {
    const path = pathname?.split('/')[1];
    if (!path) return {};

    const activeState = {};
    submenues.forEach(key => {
      // Verificar si el path actual está en los recursos del submenú
      const submenuResources = key === 'reports' ? dynamicSubMenuMetrics : [];
      activeState[key] = submenuResources.includes(path);

      // Alternativamente, verificar si el recurso tiene subMenu en sus opciones
      if (!activeState[key]) {
        const resource = allResources.find(r => r.name === path);
        activeState[key] = resource?.options?.subMenu === key;
      }
    });

    return activeState;
  }, [pathname, allResources, dynamicSubMenuMetrics]);

  const [menuState, setMenuState] = useState(() =>
    submenues.reduce((acc, key) => ({ ...acc, [key]: false }), {})
  );

  // Efecto para actualizar el estado del menú
  useEffect(() => {
    if (allResources.length === 0) return;

    const activeState = getActiveMenuState();
    setMenuState(activeState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, allResources]);

  const handleToggle = menu => {
    setMenuState(prev => ({
      ...submenues.reduce((acc, key) => ({ ...acc, [key]: false }), {}),
      [menu]: !prev[menu],
    }));
  };

  const commonResources = useMemo(
    () => allResources.filter(i => dynamicDefaultResources.includes(i?.name)),
    [allResources, dynamicDefaultResources]
  );

  const configResource = useMemo(
    () => allResources.find(i => i?.name === 'settings'),
    [allResources]
  );

  return (
    <Box sx={{ width: '100%', marginTop: 3 }}>
      <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
        <Link to="/">
          <img src={logo} alt="logo" className={classes.logo} />
        </Link>
      </Box>
      <Divider sx={{ display: { xs: 'block', sm: 'none' } }} />
      <DashboardMenuItem dense={_dense} />
      {dynamicSubMenuMetrics.length > 0 && (
        <RenderSubmenu
          allResources={allResources}
          resources={dynamicSubMenuMetrics}
          name="reports"
          label={'Reportes'}
          icon={<BarChartIcon />}
          handleToggle={handleToggle}
          open={_open}
          state={menuState}
        />
      )}
      {commonResources?.map((resource, key) => (
        <RenderItem resource={resource} key={key} onMenuClick={onMenuClick} />
      ))}

      {configResource && currentApp === 'card' && (
        <RMenu.Item
          key={configResource.name}
          to="/settings"
          className={classes.menuItem}
          name={configResource.name}
          primaryText={
            (configResource?.options && configResource.options.label) ||
            configResource.name
          }
          leftIcon={
            configResource?.icon ? <configResource.icon /> : <DefaultIcon />
          }
        />
      )}
    </Box>
  );
};
